import {
  createContext, useContext, useEffect, useState, useMemo,
} from 'react'

import { Product } from '@/types'
import {
  getPriceOrPriceRange,
  formatMoney,
  accordionDataParser,
} from '@/helpers'

import {
  useWnkdTracking,
} from '@/hooks'

import { error } from '@/services/Log'

import ProductHelpers from '@/helpers/Product'
import { ProductVariant } from '@/types/Product'
import { AccordionData } from '@/components/Pdp/AccordionProductDetails'
import { Cart, Facebook } from '@/services'
import { addToCartFailed, addToCartSuccess } from '@/services/Cart'
import { GraphQlCustomer } from '@/helpers/graphql'
import { createAddToCartEvent } from '@/components/Sitewide/events/addToCartEvent'

type ProductContextAddToCartType = (
  quantity: number,
  attribute: { [key: number]: number },
  addedVariantId: number,
) => Promise<addToCartSuccess | addToCartFailed>
interface ProductContextType {
  product?: Product,
  sku: string,
  upc: string,
  inStock: boolean,
  price: string,
  calculatedPrice: string,
  variantId: number,
  variantImage: string,
  productAttribute: number[],
  updateProductSelection: (quantity: number, attribute: number[]) => void,
  isFreeShipping: boolean,
  productAdded?: any,
  setProductAdded?: any,
  cartAddition?: any,
  setCartAddition?: any,
  loading?: any,
  setLoading?: any,
  addToCart: ProductContextAddToCartType,
  cart?: any
  setCart?: any
  selectedVariant?: ProductVariant,
  accordionData?: AccordionData[],
  user: GraphQlCustomer | null,
}

interface ProductContextProviderProps {
  product?: Product,
  children: React.ReactNode | React.ReactNode[]
  cartAddition?: any,
  setCartAddition?: any,
  loading?: any,
  setLoading?: any,
  cart?: any
  setCart?: any
  user: GraphQlCustomer | null,
}

const ProductContext = createContext<ProductContextType>({
  sku: '',
  upc: '',
  inStock: false,
  price: '',
  calculatedPrice: '',
  variantId: 0,
  variantImage: '',
  productAttribute: [],
  updateProductSelection: (quantity: number, attribute: number[]) => { },
  isFreeShipping: false,
  addToCart: async () => ({ success: false }),
  user: null,
})

const isVariantInStock = (variant: ProductVariant) => !!variant.inventory_level && variant.inventory_level > 0 && !variant?.purchasing_disabled
const extractFromVariant = (variant: ProductVariant) => ({
  sku: variant.sku || '',
  variantId: variant.id || 0,
  inStock: isVariantInStock(variant),
  upc: variant.upc || '',
})

const ProductContextProvider: React.FC<ProductContextProviderProps> = ({
  product,
  children,
  cartAddition,
  setCartAddition,
  loading,
  setLoading,
  cart,
  setCart,
  user,
}) => {
  if (!product) {
    return null
  }

  const purchasableOptions = useMemo(() => {
    const purchasableOptionsLookup = constructPurchasableOptionsLookupFromVariants(product.variants)
    return product.options.map(option => ({
      ...option,
      option_values: option.option_values
        .filter(value => {
          return !!purchasableOptionsLookup[option.id]?.[value.id]
        })
        .map(value => {
          const variant = product.variants.find((variant) => variant.option_values.find((optionValue) => optionValue.id === value.id))
          return {
            ...value,
            colors: variant?.colorChips,
          }
        })
    }))
  }, [product, constructPurchasableOptionsLookupFromVariants])

  const defaultVariant = useMemo<ProductVariant>(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search)
      const urlHasSku = !!urlParams.has('sku')
      const skuFromUrl = urlParams.get('sku') || ''
      const productVariantFromUrlSku = product.variants.find(v => v.sku === skuFromUrl)
      if (urlHasSku && skuFromUrl && productVariantFromUrlSku) {
        return productVariantFromUrlSku
      }
    }
    const firstAvailable = product?.variants.find(isVariantInStock)
    if (firstAvailable) {
      return firstAvailable
    }
    return product?.variants[0]
  }, [])

  const [{
    sku, variantId, upc, inStock,
  }, setProductData] = useState<{
    sku: string,
    variantId: number,
    upc: string,
    inStock: boolean
  }>(extractFromVariant(defaultVariant))



  const setProductFromSelectedVariant = (selectedVariant: ProductVariant) => setProductData(extractFromVariant(selectedVariant))

  const [calculatedPrice, setCalculatedPrice] = useState<string>(getPriceOrPriceRange(product, 'calculated_price'))
  const [price, setPrice] = useState<string>(getPriceOrPriceRange(product, 'price'))
  const [productAttribute, setProductAttribute] = useState<number[]>([])

  const variant = product.variants?.find((candidateVariant) => candidateVariant.id === variantId)
  const variantImage = variant?.images?.[0]?.url_square || ''

  const isFreeShipping = !!product.warranty.variants[parseInt(variant?.sku || '')]?.is_free_shipping
  const selectedVariant = product?.variants?.find(({ id }) => variantId === id)

  const updateProductSelection = (quantity, attribute) => {
    const variantFilters = attribute.reduce((acc, optionValueId, optionId) => ({
      [optionId]: optionValueId,
      ...acc,
    }), {})

    const selectedVariant = product.variants.find(({ option_values }) => {
      let isVariant = true
      option_values.forEach(({ id, option_id }) => {
        if (!!variantFilters[option_id] && variantFilters[option_id] !== id) {
          isVariant = false
        }
      })
      return isVariant
    })

    const selectedVariantPrice = product?.prices?.variants.find(({ id }) => (
      id === selectedVariant?.id
    ))
    if (selectedVariant) {
      setProductFromSelectedVariant(selectedVariant)
    }

    if (!ProductHelpers.getCollectionVariants(product).length) {
      setPrice(formatMoney(selectedVariantPrice?.price))
      setCalculatedPrice(formatMoney(selectedVariantPrice?.calculated_price))
    }
    selectedVariant?.option_values?.forEach(({ option_id, id }) => {
      attribute[option_id] = id
    })
    setProductAttribute(attribute)
  }

  useEffect(() => {
    const urlHasSku = !!sku
    if (urlHasSku) {
      const attribute = ProductHelpers.getProductAttributeBySku(product, sku)
      updateProductSelection(1, attribute)
    }
  }, [purchasableOptions, sku])

  useEffect(() => {
    if (sku) {
      const url = new URL(window.location.href)
      const urlParams = new URLSearchParams(window.location.search)
      urlParams.set('sku', sku)
      const removeUrlParamIfPresent = (param: string) => {
        if (!!urlParams.has(param)) {
          urlParams.delete(param)
        }
      }
      const paramsToRemove = ['optionId', 'optionIds', 'optionValueId', 'optionValueIds']
      paramsToRemove.forEach(removeUrlParamIfPresent)
      const newParams = `${urlParams.toString()}`
      const newUrl = (url.toString().split('?')[0]) + (newParams ? `?${newParams}` : '')
      window.history.pushState({}, document.title, newUrl)
    }
  }, [sku])

  const {
    setAddToCartData: setAddToCartDataForWunderkind,
  } = useWnkdTracking({
    product,
    cart,
    selectedVariantId: variantId,
    sku,
    calculatedPrice,
    variantImage,
    isUserSignedIn: !!user?.entityId
  })
  // Data fetching and writing methods
  const addToCart = async (
    quantity: number,
    attribute: { [key: number]: number },
    addedVariantId: number,
  ): Promise<addToCartSuccess | addToCartFailed> => { // PDP add to cart
    setLoading(true)
    try {
      const productId = parseInt(`${product?.id}`)
      const variantId = addedVariantId || product?.variants?.[0]?.id || 0
      const addToCartResponse = await Cart.add({
        quantity, productId, variantId
      })
      if (addToCartResponse.success) {
        setCart(addToCartResponse.newCart)
      }
      setLoading(false)
      setAddToCartDataForWunderkind({
        quantity,
        attribute,
      })
      setTimeout(() => {
        document.dispatchEvent(createAddToCartEvent())
      }, 1000)

      try {
        const item_price = parseFloat(`${(calculatedPrice)}`.replace(/(^\$|\,)/g, ''))
        Facebook.addToCart({
          content_ids: [sku],
          contents: [{ id: sku, quantity, item_price }],
          content_name: product.name,
          content_category: product?.breadcrumbs?.pop()?.name || '',
          value: item_price * quantity,
          user,
        })
      } catch (err) {
        error(`Failed to Send AddTocart Event to Facebook ${err}`)
      }
      return addToCartResponse
    } catch (e) {
      error('Regular add to cart did not work', e)
      setLoading(false)
    }
    return { success: false }
  }
  const accordionData = useMemo<any>(() => {
    try {
      const accordionData = accordionDataParser(product, sku)
      return accordionData
    } catch (err) {
      error('Failed to parse product to accordion data', err)
    }
    return null
  }, [product, sku])

  const augmentProductVariants = (product: Product) => {
    const warrantyData = product.warranty
    const isColorOption = (label: string) => `${label}`.toLowerCase() === 'color'
    return {
      ...product,
      options: purchasableOptions,
      variants: product.variants.map((variant) => {
        const option_values = variant.option_values.map((option) => {
          if (isColorOption(option.option_display_name)) {
            return {
              ...option,
              value: variant?.colorChips
            }
          }
          return {
            ...option,
            value: product.options.find(o => o.id === option.option_id)?.option_values.find(v => v.id === option.id)?.label
          }
        })

        return {
          ...(warrantyData?.[variant?.sku] || {}),
          ...variant,
          option_values,
        }
      })
    }
  }

  return (
    <ProductContext.Provider value={{
      product: augmentProductVariants(product),
      sku,
      inStock,
      upc,
      price,
      calculatedPrice,
      variantId,
      variantImage,
      selectedVariant,
      productAttribute,
      updateProductSelection,
      isFreeShipping,
      cartAddition,
      setCartAddition,
      loading,
      setLoading,
      addToCart,
      cart,
      setCart,
      accordionData,
      user,
    }}
    >
      {children}
    </ProductContext.Provider>
  )
}

export default ProductContextProvider

export const useProduct = (): ProductContextType => useContext(ProductContext)

type PurchasableOptions = {
  [index: string]: undefined | {
    [index: string]: undefined | {}
  }
}
const constructPurchasableOptionsLookupFromVariants =
  (variants: ProductVariant[]): PurchasableOptions =>
    variants
      .filter(variant => !variant.purchasing_disabled)
      .reduce((hash, variant) => {
        try {
          variant.option_values.forEach((optionValue) => {
            try {
              if (hash[optionValue.option_id]) {
                hash[optionValue.option_id][optionValue.id] = {}
              } else {
                hash[optionValue.option_id] = {
                  [optionValue.id]: {}
                }
              }
            } catch { }
          })
        } finally {
          return hash
        }
      }, {})